import React, { useContext } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';

import classNames from 'classnames';

import { Sticky } from 'semantic-ui-react';

import '../css/CautionBanner.less';
import imageCaution from '../img/image-caution.png';

import { SatCoreRegister } from '../SatCoreRegistry';
import UtilityService from '../services/UtilityService';

/**
 * CautionBanner - Displays warning/info messages at top of page
*
 * @param {boolean} allowMinimizeMaximize
 * @param {string} bannerKey
 * @param {string} cautionBannerMsg - (default) Direct HTML string message
 * @param {Array} cautionBannerMsgConfigArray - Advanced configuration for one or more messages
 * @param {boolean} isImpersonationCautionBannerShowing
 * @param {number} stickyOffset
 * @param {Function} t
 *
 * If cautionBannerMsg is provided, it takes precedence and cautionBannerMsgConfigArray is ignored.
 *
 * @example
 * // Simple usage with string message
 * <CautionBanner
 *   bannerKey="ExpiredLicense"
 *   cautionBannerMsg="Your license has expired."
 *   stickyOffset={50} />
 *
 * @example
 * // Advanced usage with config objects
 * <CautionBanner
 *   allowMinimizeMaximize={false}
 *   bannerKey="DefaultCautionBanner"
 *   cautionBannerMsgConfigArray={[
 *     {
 *       _id: 'expiredMsg',
 *       translationKey: 'expiredLicenseCautionBannerMsg'
 *     }
 *   ]}
 *   stickyOffset={50}
 *   t={t} />
 */
const CautionBanner = observer(({
  allowMinimizeMaximize = true,
  bannerKey,
  cautionBannerMsg,
  cautionBannerMsgConfigArray,
  isImpersonationCautionBannerShowing,
  stickyOffset,
  t
} = {}) => {
  const { notificationManager } = useContext(MobXProviderContext);

  const renderCautionBannerContent = () => {
    // If cautionBannerMsg exists, use it (e.g. for ImpersonationCautionBanner, etc.)
    if (cautionBannerMsg) {
      return cautionBannerMsg;
    }

    // else, render from configuration objects (supports advanced messages with custom logic)
    if (!cautionBannerMsgConfigArray || cautionBannerMsgConfigArray.length === 0) {
      return null;
    }

    return cautionBannerMsgConfigArray?.map?.((config, index) => {
      const key = config._id || `banner-${index}`;
      return renderConfiguredCautionBannerMsg(config, key);
    });
  };

  const renderConfiguredCautionBannerMsg = (config, key) => {
    const html = `${t(config.translationKey)}<br />`;
    const parsed = UtilityService.reactHtmlParserWrapper(html)?.parsed;
    return <React.Fragment key={key}>{parsed}</React.Fragment>;
  };

  const bannerTitle = notificationManager.isCautionBannerMinimized ? t('clickToExpand') : t('clickToMinimize');

  return !notificationManager.isCautionBannerLoading ? (
    <Sticky className={classNames('caution-banner-sticky', `${bannerKey}-sticky`, {
      isImpersonationCautionBannerShowing,
      'caution-banner-no-minimize': !allowMinimizeMaximize
    })} offset={stickyOffset || 50}>
      <div
        className={classNames('caution-banner-inner', `${bannerKey}-inner`, {
          'caution-banner-inner-minimized': notificationManager.isCautionBannerMinimized
        })}
        onClick={allowMinimizeMaximize ? () => {
          notificationManager.setIsCautionBannerLoading(true);
          notificationManager.setIsCautionBannerMinimized(!notificationManager.isCautionBannerMinimized);
          setTimeout(() => {
            notificationManager.setIsCautionBannerLoading(false);
          }, 1);
        } : undefined}
        title={allowMinimizeMaximize ? bannerTitle : undefined}>
        <div className='caution-banner-icon'>
          <img alt='' src={imageCaution} />
        </div>
        <div className='caution-banner-text'>
          {renderCautionBannerContent()}
        </div>
      </div>
    </Sticky>
  ) : null;
});
export default CautionBanner;

SatCoreRegister('CautionBanner', CautionBanner);
